import type { TFunction } from "i18next"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import type { ProposalDurationValidation, ProposalSalesPriceValidation } from "./types.ts"

/** One renderable line — shared shape for both the compact card and the
 * Generate-time review modal, so the two can never show different wording
 * or different numbers for the same check. */
interface ValidationLine {
  title: string
  /** Present only for a `warning` line — the before/after/difference
   * detail. Absent for `insufficient_data` (a single neutral sentence). */
  comparison?: string
}

// Shares the Contract card's own `durationDays` leaf — the plural rule for
// "N day(s)" has nothing document-specific about it, so pointing at the
// same key avoids a duplicate i18n leaf.
function formatDurationDays(t: TFunction, days: number): string {
  return t("contractValidation.durationDays", { count: Math.abs(days) })
}

function formatSalesPriceLine(
  t: TFunction,
  validation: ProposalSalesPriceValidation
): ValidationLine | null {
  if (validation.status === "warning") {
    return {
      title: t("proposalValidation.salesPrice.warningTitle"),
      comparison: t("proposalValidation.salesPrice.comparison", {
        salesPrice: budgetFormat(validation.sales_price),
        milestoneTotal: budgetFormat(validation.milestone_total),
        difference: budgetFormat(
          validation.difference === null ? null : Math.abs(validation.difference)
        )
      })
    }
  }
  if (validation.status === "insufficient_data") {
    return { title: t("proposalValidation.salesPrice.insufficientData") }
  }
  return null
}

function formatDurationLine(
  t: TFunction,
  validation: ProposalDurationValidation
): ValidationLine | null {
  if (validation.status === "warning") {
    return {
      title: t("proposalValidation.duration.warningTitle"),
      comparison: t("proposalValidation.duration.comparison", {
        contractDuration: formatDurationDays(t, validation.contract_duration_days ?? 0),
        milestoneDuration: formatDurationDays(t, validation.milestone_duration_days ?? 0),
        difference: formatDurationDays(t, validation.difference_days ?? 0)
      })
    }
  }
  if (validation.status === "insufficient_data") {
    return { title: t("proposalValidation.duration.insufficientData") }
  }
  return null
}

export type { ValidationLine }
export { formatSalesPriceLine, formatDurationLine }
